import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import { productList } from "@/data/productList";
import { useLightbox } from "@/hooks/useLightbox";
import { Lightbox } from "@/components/ImageLightbox";


export default function Products() {
  const { isOpen, src, alt, openLightbox, closeLightbox } = useLightbox();

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/80 text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Products</h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Comprehensive range of high-quality industrial minerals for every
            industry need
          </p>
        </div>
      </section>

      {/* Products Section */}
      {/* 🌟 Our Premium Products Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-white to-gray-50 w-full">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary inline-block relative">
              Our Premium Products
              <span className="block w-24 h-1 bg-accent mx-auto mt-3 rounded-full"></span>
            </h2>
            <p className="text-foreground/70 mt-4 max-w-2xl mx-auto text-lg">
              Explore our high-quality minerals crafted for purity, consistency, and
              superior industrial performance.
            </p>
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {productList.map((product) => (
              <div
                key={product.id}
                className="group bg-white rounded-2xl overflow-hidden border border-border shadow-[0_8px_30px_rgb(0,0,0,0.05)] hover:shadow-[0_10px_40px_rgba(0,0,0,0.15)] transition-all duration-300 hover:-translate-y-1"
              >
                {/* Image */}
                <div
                  className="relative bg-white flex items-center justify-center rounded-t-2xl overflow-hidden cursor-pointer group"
                  onClick={() => openLightbox(product.image, product.name)}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-64 object-contain transition-transform duration-300 group-hover:scale-105 bg-white"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/placeholder.svg";
                    }}
                  />
                </div>
                {/* Content */}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-primary mb-3">
                    {product.name}
                  </h3>
                  <p className="text-foreground/70 mb-6 leading-relaxed">
                    {product.description}
                  </p>

                  {/* Specifications */}
                  <div className="mb-5">
                    <p className="text-sm font-semibold text-primary mb-2">
                      Specifications:
                    </p>
                    <ul className="space-y-1">
                      {product.specifications.map((spec, idx) => (
                        <li
                          key={idx}
                          className="text-sm text-foreground/60 flex items-center gap-2"
                        >
                          <span className="text-accent font-bold">✓</span> {spec}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Applications */}
                  <div className="mb-6">
                    <p className="text-sm font-semibold text-primary mb-2">
                      Applications:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {product.applications.slice(0, 3).map((app, idx) => (
                        <span
                          key={idx}
                          className="text-xs font-medium bg-accent/10 text-accent px-2.5 py-1 rounded-md 
                      border border-blue-100 group-hover:bg-accent/5 transition-colors"
                        >
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Link */}
                  <Link
                    to={product.path}
                    className="inline-flex items-center gap-2 text-accent font-semibold hover:gap-3 transition-all"
                  >
                    View Details
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* ✅ Place Lightbox OUTSIDE the map */}
          <Lightbox src={src} alt={alt} isOpen={isOpen} onClose={closeLightbox} />


          {/* Bottom CTA */}
          <div className="mt-20 p-10 bg-gradient-to-r from-accent/10 to-primary/10 rounded-2xl text-center shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
            <h3 className="text-3xl font-bold text-primary mb-4">
              Need Custom Products?
            </h3>
            <p className="text-foreground/70 mb-6 max-w-2xl mx-auto text-lg">
              Looking for specific compositions or tailored solutions? Our experts can
              customize minerals to your exact industrial needs.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
            >
              Request Custom Product
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-[#f5f8ff] via-[#ebf1ff] to-[#dee7ff] text-center relative overflow-hidden mt-[-24px]">
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-4xl font-extrabold text-gray-900 mb-4">
            Ready to Order?
          </h2>
          <p className="text-lg text-gray-600 mb-10">
            Get a competitive quote for your mineral requirements today
          </p>

          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-semibold
      bg-gradient-to-r from-accent/100 to-accent/90 text-white
      shadow-[0_4px_20px_rgba(37,99,235,0.4)]
      hover:shadow-[0_6px_25px_rgba(37,99,235,0.6)]
      hover:translate-y-[-2px] transition-all duration-300"
          >
            Get a Quote
            <ArrowRight size={20} />
          </Link>
        </div>

        {/* Soft glowing background accent */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[80%] h-36 bg-blue-200/30 blur-3xl rounded-full"></div>
      </section>
    </Layout>
  );
}
